import "@/i18n";
import "@/lib/teacherAuthFetch";
import "@/App.css";
import { useEffect, Suspense, lazy } from "react";
import Canonical from "@/components/Canonical";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster, toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import { noteAppOpen } from "@/lib/attentionMetrics";
import { warmVoices } from "@/lib/phonics";
import useFirstThirtySeconds from "@/hooks/useFirstThirtySeconds";
import ScrollToTop from "@/components/ScrollToTop";
import GAPageviewTracker from "@/components/GAPageviewTracker";
import { ActivationTracker } from "@/lib/activationTracking";
import MilestoneCelebrator from "@/components/MilestoneCelebrator";
import MilestoneCertificate from "@/components/MilestoneCertificate";
import EngagementTracker from "@/components/EngagementTracker";

import Layout from "@/components/Layout";
const Home = lazy(() => import("@/pages/Home"));
import TodayHome from "@/pages/TodayHome";
const Academy = lazy(() => import("@/pages/Academy"));
const MissionPlayer = lazy(() => import("@/pages/MissionPlayer"));
const AcademyCertificate = lazy(() => import("@/pages/AcademyCertificate"));
const CertificateShare = lazy(() => import("@/pages/CertificateShare"));
const AcademyUnit = lazy(() => import("@/pages/AcademyUnit"));
const LearnPathways = lazy(() => import("@/pages/LearnPathways"));
const UnitPage = lazy(() => import("@/pages/UnitPage"));
const Diagnostic = lazy(() => import("@/pages/Diagnostic"));
const PlayCreate = lazy(() => import("@/pages/PlayCreate"));
const MyQuest = lazy(() => import("@/pages/MyQuest"));
const Portfolio = lazy(() => import("@/pages/Portfolio"));
const CurriculumExplorer = lazy(() => import("@/pages/CurriculumExplorer"));
const GrownUps = lazy(() => import("@/pages/GrownUps"));
const GrownupGateway = lazy(() => import("@/pages/GrownupGateway"));
const AISafety = lazy(() => import("@/pages/AISafety"));
const GuidePage = lazy(() => import("@/pages/GuidePage"));
const WorldTrip = lazy(() => import("@/pages/WorldTrip"));
// Cycle 56 · Marketing / product split.
const HomeMarketing = lazy(() => import("@/pages/HomeMarketing"));
const MyLearning = lazy(() => import("@/pages/MyLearning"));
const LearnCountries = lazy(() => import("@/pages/LearnCountries"));
const ContinentMaps = lazy(() => import("@/pages/ContinentMaps"));
// Heavy routes → lazy-loaded so the initial home page paints faster.
// Each lazy chunk is only downloaded when a kid actually navigates there.
const Coloring        = lazy(() => import("@/pages/Coloring"));
const CreateHub       = lazy(() => import("@/pages/CreateHub"));
const CreateComingSoon = lazy(() => import("@/pages/CreateComingSoon"));
const Games           = lazy(() => import("@/pages/Games"));
const Quiz            = lazy(() => import("@/pages/Quiz"));
const FirstQuest      = lazy(() => import("@/pages/FirstQuest"));
const TicTacToe       = lazy(() => import("@/pages/TicTacToe"));
const Kaleidoscope    = lazy(() => import("@/pages/Kaleidoscope"));
const PixelArt        = lazy(() => import("@/pages/PixelArt"));
const Parents         = lazy(() => import("@/pages/Parents"));
const Privacy         = lazy(() => import("@/pages/Privacy"));
const TravelHome      = lazy(() => import("@/pages/TravelHome"));
const TravelCategory  = lazy(() => import("@/pages/TravelHome").then((m) => ({ default: m.TravelCategory })));
const Schedule        = lazy(() => import("@/pages/Schedule"));
const LearningPlan    = lazy(() => import("@/pages/LearningPlan"));
const PrintablePlan   = lazy(() => import("@/pages/PrintablePlan"));
const StudyProgram    = lazy(() => import("@/pages/StudyProgram"));
const DuelQuiz        = lazy(() => import("@/pages/duel/DuelQuiz"));
const DuelTables      = lazy(() => import("@/pages/duel/DuelTables"));
const DuelSpell       = lazy(() => import("@/pages/duel/DuelSpell"));
const DuelContinent   = lazy(() => import("@/pages/duel/DuelContinent"));
const DuelReaction    = lazy(() => import("@/pages/duel/DuelReaction"));
const TermReport      = lazy(() => import("@/pages/TermReport"));
const ParentProof     = lazy(() => import("@/pages/ParentProof"));
const EvidencePage    = lazy(() => import("@/pages/EvidencePage"));
const AdminAnalytics  = lazy(() => import("@/pages/AdminAnalytics"));
const AcademyHome     = lazy(() => import("@/pages/AcademyHome"));
const AcademyCourse   = lazy(() => import("@/pages/AcademyCourse"));
const AcademySkill    = lazy(() => import("@/pages/AcademySkill"));
const AcademyUnitTest = lazy(() => import("@/pages/AcademyUnitTest"));
const AcademyPlacement = lazy(() => import("@/pages/AcademyPlacement"));
const Compass         = lazy(() => import("@/pages/Compass"));
const ExploreHub       = lazy(() => import("@/pages/ExploreHub"));
const FindSomething    = lazy(() => import("@/pages/FindSomething"));
const FocusDay         = lazy(() => import("@/pages/FocusDay"));
const LiveQuestBuilder = lazy(() => import("@/pages/LiveQuestBuilder"));
const CurriculumLanding = lazy(() => import("@/pages/CurriculumLanding"));
const CurriculumIndex   = lazy(() => import("@/pages/CurriculumLanding").then((m) => ({ default: m.CurriculumIndex })));
const CountryLanding    = lazy(() => import("@/pages/CountryLanding"));
const CountryIndex      = lazy(() => import("@/pages/CountryLanding").then((m) => ({ default: m.CountryIndex })));
const AgeLanding        = lazy(() => import("@/pages/AgeLanding"));
const AgeIndex          = lazy(() => import("@/pages/AgeLanding").then((m) => ({ default: m.AgeIndex })));
const StudentPlan     = lazy(() => import("@/pages/StudentPlan"));
const Languages       = lazy(() => import("@/pages/Languages"));
const LearnTopics     = lazy(() => import("@/pages/LearnTopics"));
const OutcomeTest     = lazy(() => import("@/pages/OutcomeTest"));
const MyWorld         = lazy(() => import("@/pages/MyWorld"));
const AdventurePass   = lazy(() => import("@/pages/AdventurePass"));
const Globe3D         = lazy(() => import("@/pages/Globe3D"));
const Puzzles         = lazy(() => import("@/pages/Puzzles"));
const MathsAcademy    = lazy(() => import("@/pages/MathsAcademy"));
const MathsAcademyLibrary = lazy(() => import("@/pages/MathsAcademyLibrary"));
// KnowledgeHub retired Feb 2026 — merged into TreasureQuest. File
// kept on disk (KnowledgeHub.jsx) for design reference; no longer
// imported so the bundle doesn't ship it.
const Animals         = lazy(() => import("@/pages/Animals"));
const OurPhilosophy   = lazy(() => import("@/pages/OurPhilosophy"));
const Contact         = lazy(() => import("@/pages/Contact"));
const Practice        = lazy(() => import("@/pages/Practice"));
const TeacherDashboard = lazy(() => import("@/pages/TeacherDashboard"));
const TeacherConsole = lazy(() => import("@/pages/TeacherConsole"));
const ParentDashboard = lazy(() => import("@/pages/ParentDashboard"));
const ParentVerify = lazy(() => import("@/pages/ParentVerify"));
const DiagnosticQuest = lazy(() => import("@/pages/DiagnosticQuest"));
const KidJoin = lazy(() => import("@/pages/KidJoin"));
const AdminCurriculum = lazy(() => import("@/pages/AdminCurriculum"));
const AdminCurriculumCoverage = lazy(() => import("@/pages/AdminCurriculumCoverage"));
const AdminLessonIntegrity = lazy(() => import("@/pages/AdminLessonIntegrity"));
const AdminLiveToday = lazy(() => import("@/pages/AdminLiveToday"));
const AdminFutureReady = lazy(() => import("@/pages/AdminFutureReady"));
const LessonReader    = lazy(() => import("@/pages/LessonReader"));
const AdminLessons    = lazy(() => import("@/pages/AdminLessons"));
const AdminQuestions  = lazy(() => import("@/pages/AdminQuestions"));
const AdminTopicVideos = lazy(() => import("@/pages/AdminTopicVideos"));
const NorthStarDashboard = lazy(() => import("@/pages/NorthStarDashboard"));
const CurriculumQuality  = lazy(() => import("@/pages/CurriculumQuality"));
const AdminQuestionBankHealth = lazy(() => import("@/pages/AdminQuestionBankHealth"));
const DailyChallenge     = lazy(() => import("@/pages/DailyChallenge"));
const Tournament         = lazy(() => import("@/pages/Tournament"));
const CodeBreakerHome    = lazy(() => import("@/pages/CodeBreaker"));
const CodeBreakerPlay    = lazy(() => import("@/pages/CodeBreaker").then((m) => ({ default: m.CodeBreakerPlay })));
const MetricsDefinitions = lazy(() => import("@/pages/MetricsDefinitions"));
const ApprovalQueue = lazy(() => import("@/pages/ApprovalQueue"));
const OutreachTargets = lazy(() => import("@/pages/OutreachTargets"));
const PilotHealth = lazy(() => import("@/pages/PilotHealth"));
const FundraiseTargets = lazy(() => import("@/pages/FundraiseTargets"));
const LiveQuestHost = lazy(() => import("@/pages/LiveQuestHost"));
const AcademyCoverage = lazy(() => import("@/pages/AcademyCoverage"));
const AdminHub = lazy(() => import("@/pages/AdminHub"));
const AdminInbox = lazy(() => import("@/pages/AdminInbox"));
const AdminLearningConfig = lazy(() => import("@/pages/AdminLearningConfig"));
const StoryArcsAdmin = lazy(() => import("@/pages/StoryArcsAdmin"));
const AgePicker = lazy(() => import("@/pages/AgePicker"));
const PlaceHub = lazy(() => import("@/pages/PlaceHub"));
const TeacherHub = lazy(() => import("@/pages/TeacherHub"));
const TutorConsole = lazy(() => import("@/pages/TutorConsole"));
const FocusStart = lazy(() => import("@/pages/FocusStart"));
const TeacherPlanner = lazy(() => import("@/pages/TeacherPlanner"));
const TeacherPrint = lazy(() => import("@/pages/TeacherPrint"));
const TeacherReport = lazy(() => import("@/pages/TeacherReport"));
// Feb 2026 · CRO refresh — lightweight B2B landing page for schools.
const ForSchools = lazy(() => import("@/pages/ForSchools"));
const CurriculumBrowser = lazy(() => import("@/pages/CurriculumBrowser"));
const CurriculumCoverage = lazy(() => import("@/pages/CurriculumCoverage"));
const SubjectsBrowser = lazy(() => import("@/pages/SubjectsBrowser"));
const LearningHub = lazy(() => import("@/pages/LearningHub"));
const CurriculumGaps = lazy(() => import("@/pages/CurriculumGaps"));
const TeacherStudio = lazy(() => import("@/pages/TeacherStudio"));
const StudentJoin = lazy(() => import("@/pages/StudentJoin"));
const VideoStudio = lazy(() => import("@/pages/VideoStudio"));
const LanguageQuest = lazy(() => import("@/pages/LanguageQuest"));
const TimesTablesQuest = lazy(() => import("@/pages/TimesTablesQuest"));
const MtcCheck = lazy(() => import("@/pages/MtcCheck"));
const EnglishQuest = lazy(() => import("@/pages/EnglishQuest"));
const EnglishMethodology = lazy(() => import("@/pages/EnglishMethodology"));
// Feb 2026 · CRO refresh — public impact-numbers page linked in footer.
const Impact = lazy(() => import("@/pages/Impact"));
// Cycle 64.2 — Schools admin dashboard (macro + micro reports).
const SchoolAdmin = lazy(() => import("@/pages/SchoolAdmin"));
const SiteMapPage = lazy(() => import("@/pages/SiteMapPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const SchoolOnboarding = lazy(() => import("@/pages/SchoolOnboarding"));
const TeacherBuildWeek = lazy(() => import("@/pages/TeacherBuildWeek"));
const TeacherHelp = lazy(() => import("@/pages/TeacherHelp"));
const KidStoryDeck = lazy(() => import("@/pages/KidStoryDeck"));
const Stories = lazy(() => import("@/pages/Stories"));
const StoryReader = lazy(() => import("@/pages/StoryReader"));
const ExperienceQuality = lazy(() => import("@/pages/ExperienceQuality"));
const ClockGame = lazy(() => import("@/pages/ClockGame"));
const ToolsHub = lazy(() => import("@/pages/tools/ToolsHub"));
const NumberLineTool = lazy(() => import("@/pages/tools/NumberLineTool"));
const PlaceValueTool = lazy(() => import("@/pages/tools/PlaceValueTool"));
const FractionWallTool = lazy(() => import("@/pages/tools/FractionWallTool"));
const BaseTenTool     = lazy(() => import("@/pages/tools/BaseTenTool"));
const BeadStringTool  = lazy(() => import("@/pages/tools/BeadStringTool"));
const CountersTool    = lazy(() => import("@/pages/tools/CountersTool"));
const ArrayTool       = lazy(() => import("@/pages/tools/ArrayTool"));
const TenFrameTool    = lazy(() => import("@/pages/tools/TenFrameTool"));
const BarModelTool    = lazy(() => import("@/pages/tools/BarModelTool"));
const ClockTool       = lazy(() => import("@/pages/tools/ClockTool"));
const Daily10 = lazy(() => import("@/pages/tools/Daily10"));
// Cycle 56 · Primer-like extensions
const CompanionPicker    = lazy(() => import("@/pages/CompanionPicker"));
const EarlyReading       = lazy(() => import("@/pages/EarlyReading"));
const PhonicsPlacement   = lazy(() => import("@/pages/PhonicsPlacement"));
const DecodablePassage   = lazy(() => import("@/pages/DecodablePassage"));
const ValuesJourney      = lazy(() => import("@/pages/ValuesJourney"));
const ParentLearnerView  = lazy(() => import("@/pages/ParentLearnerView"));
const TeacherWeeklyPlan = lazy(() => import("@/pages/TeacherWeeklyPlan"));
const SpellingBee     = lazy(() => import("@/pages/SpellingBee"));
const Mandala         = lazy(() => import("@/pages/Mandala"));
const Dodge           = lazy(() => import("@/pages/Dodge"));
// Feb 2026 · Batch 2 arcade juice pass — Dodge+ and Squishy Hop+ ship
// as self-contained standalone HTML prototypes (see /public/games/).
// The React app iframes them via <ArcadeIframeGame />, keeping the
// legacy /dodge and /squishy routes alive but routing them at the
// upgraded experience.
const ArcadeIframeGame = lazy(() => import("@/components/ArcadeIframeGame"));
const DodgePublic     = lazy(() => import("@/pages/games/DodgePublic"));
const MatchPairsPublic = lazy(() => import("@/pages/games/MatchPairsPublic"));
const WordBuilderPublic = lazy(() => import("@/pages/games/WordBuilderPublic"));
const SequenceItPublic = lazy(() => import("@/pages/games/SequenceItPublic"));
const LabelItPublic   = lazy(() => import("@/pages/games/LabelItPublic"));
const SpeedTablesPublic = lazy(() => import("@/pages/games/SpeedTablesPublic"));
// Cycle 15 · new games on the shared GameShell
const MatchPairsGame  = lazy(() => import("@/pages/games/MatchPairsGame"));
const WordBuilderGame = lazy(() => import("@/pages/games/WordBuilderGame"));
const SequenceItGame  = lazy(() => import("@/pages/games/SequenceItGame"));
const LabelItGame     = lazy(() => import("@/pages/games/LabelItGame"));
const SpeedTablesGame = lazy(() => import("@/pages/games/SpeedTablesGame"));
const SignLanguage    = lazy(() => import("@/pages/SignLanguage"));
const SignLesson      = lazy(() => import("@/pages/SignLesson"));
const SignMatchGame   = lazy(() => import("@/pages/games/SignMatchGame"));
const SignSprintGame  = lazy(() => import("@/pages/games/SignSprintGame"));
const PhonicsCourse   = lazy(() => import("@/pages/PhonicsCourse"));
const PhonicsStage    = lazy(() => import("@/pages/PhonicsStage"));
const LifeSkills      = lazy(() => import("@/pages/LifeSkills"));
const MoneyTime       = lazy(() => import("@/pages/MoneyTime"));
const Handwriting     = lazy(() => import("@/pages/Handwriting"));
const KeyQuestGame    = lazy(() => import("@/pages/games/KeyQuestGame"));
const CodeQuest       = lazy(() => import("@/pages/CodeQuest"));
const PatternTrainer  = lazy(() => import("@/pages/PatternTrainer"));
const FairOrUnfair    = lazy(() => import("@/pages/FairOrUnfair"));
const PromptBuilder   = lazy(() => import("@/pages/PromptBuilder"));
const TrainTheRobot   = lazy(() => import("@/pages/TrainTheRobot"));
const AIDetective     = lazy(() => import("@/pages/AIDetective"));
const HumanOrAI       = lazy(() => import("@/pages/HumanOrAI"));
const LearnAI         = lazy(() => import("@/pages/LearnAI"));
const TreasureQuest   = lazy(() => import("@/pages/TreasureQuest"));
const NamePlaceAnimal = lazy(() => import("@/pages/NamePlaceAnimal"));
const StainedGlass    = lazy(() => import("@/pages/StainedGlass"));
const DesignPreview   = lazy(() => import("@/pages/DesignPreview"));
const QuestCards      = lazy(() => import("@/pages/QuestCards"));
const MasteryTrial    = lazy(() => import("@/pages/MasteryTrial"));
const LiveQuest       = lazy(() => import("@/pages/LiveQuest"));
const LiveQuestSolo   = lazy(() => import("@/pages/LiveQuestSolo"));
const LiveRoom        = lazy(() => import("@/pages/LiveRoom"));
const ClassJoin       = lazy(() => import("@/pages/ClassJoin"));
const SignIn          = lazy(() => import("@/pages/SignIn"));
const Family          = lazy(() => import("@/pages/Family"));
// Feb 2026 · Home Reshuffle · "Plan" primary top-nav destination.
const MyPlan          = lazy(() => import("@/pages/MyPlan"));
const QuestionPaper   = lazy(() => import("@/pages/QuestionPaper"));
const AuthCallback    = lazy(() => import("@/pages/AuthCallback"));
const PaperViewer     = lazy(() => import("@/pages/PaperViewer"));
const LinkDevice      = lazy(() => import("@/pages/LinkDevice"));
const JoinWithCode    = lazy(() => import("@/pages/JoinWithCode"));
const DailyReview     = lazy(() => import("@/pages/DailyReview"));
const ClassIsland     = lazy(() => import("@/pages/ClassIsland"));
const LessonBrowser   = lazy(() => import("@/pages/LessonBrowser"));
const Share           = lazy(() => import("@/pages/Share"));
const CertificateHall = lazy(() => import("@/pages/CertificateHall"));
// Feb 2026 · Real Teacher connection (booking + chat + video)
const RealTeachers            = lazy(() => import("@/pages/RealTeachers"));
const RealTeacherAdmin        = lazy(() => import("@/pages/RealTeacherAdmin"));
const TeacherVideoAdmin       = lazy(() => import("@/pages/TeacherVideoAdmin"));
const HelpHub                 = lazy(() => import("@/pages/HelpHub"));
const About                   = lazy(() => import("@/pages/About"));
const PartnersTravelMode      = lazy(() => import("@/pages/PartnersTravelMode"));
const Schools                 = lazy(() => import("@/pages/Schools"));
const ReadingSession          = lazy(() => import("@/pages/ReadingSession"));
const ReadingPlacement        = lazy(() => import("@/pages/ReadingPlacement"));
const ReadingHub              = lazy(() => import("@/pages/ReadingHub"));
const DrawAlong               = lazy(() => import("@/pages/DrawAlong"));
const DrawAlongIndex          = lazy(() => import("@/pages/DrawAlong").then((m) => ({ default: m.DrawAlongIndex })));

// Preserve old /knowledge/:id[/:level] deep links after the Feb 2026
// merge: redirect straight to Treasure Quest with the section + level
// carried across as query params.
function KnowledgeToTreasureRedirect() {
  const { id, level } = useParams();
  const q = new URLSearchParams();
  if (id) q.set("section", id);
  if (level) q.set("level", level);
  return <Navigate to={`/treasure-quest?${q.toString()}`} replace />;
}

// Sign-in gate that hands OAuth returns off to <AuthCallback /> BEFORE
// the SignIn UI mounts. Detected synchronously so we don't paint the
// email form for a split second on the Google callback leg.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
function SignInGate() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return <SignIn />;
}
const LanguageLesson = lazy(() => import("@/pages/LanguageLesson"));
const MathGeography = lazy(() => import("@/pages/MathGeography"));
const AdvancedHub = lazy(() => import("@/pages/AdvancedHub"));
const Stickers = lazy(() => import("@/pages/Stickers"));
const Passport = lazy(() => import("@/pages/Passport"));
const Campaigns = lazy(() => import("@/pages/Campaigns"));
const Adventure = lazy(() => import("@/pages/Adventure"));
// Feb 2026 · Ask Globy REVIVED per user request — brings back the
// child-safe AI Q&A tutor. The page (`/ask`) already exists with
// backend `/api/ask`. Just re-mounting the route + import.
const AskTheGlobe = lazy(() => import("@/pages/AskTheGlobe"));
const DreamTrip = lazy(() => import("@/pages/DreamTrip"));
const SquishyHop = lazy(() => import("@/pages/SquishyHop"));
const MathSnap = lazy(() => import("@/pages/MathSnap"));
const SpellCountries = lazy(() => import("@/pages/SpellCountries"));
const GeoLines = lazy(() => import("@/pages/GeoLines"));
const HubPage = lazy(() => import("@/pages/HubPage"));
const PillarPage = lazy(() => import("@/pages/PillarPage"));
const ExploreNature = lazy(() => import("@/pages/ExploreNature"));
const ExploreAnimals = lazy(() => import("@/pages/ExploreAnimals"));
const ExploreWonders = lazy(() => import("@/pages/ExploreWonders"));
const ExploreCultures = lazy(() => import("@/pages/ExploreCultures"));
const MyWorldStatistics = lazy(() => import("@/pages/MyWorldStatistics"));
const MyWorldHistory = lazy(() => import("@/pages/MyWorldHistory"));
const Safety = lazy(() => import("@/pages/Safety"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Printables = lazy(() => import("@/pages/Printables"));
const PrintablePack = lazy(() => import("@/pages/PrintablePack"));
const CountryPage = lazy(() => import("@/pages/CountryPage"));
import CertificateModal from "@/components/CertificateModal";
import AgeSelector from "@/components/AgeSelector";
import BornInPicker from "@/components/BornInPicker";
import InstallAppPrompt from "@/components/InstallAppPrompt";
import DailyPointsMilestones from "@/components/DailyPointsMilestones";
// Globy floating-globe companion removed Feb 2026 per user request —
// the widget felt visually noisy at the bottom-left corner. If we
// ever want it back it's a one-line re-add of the import + <Globy />.
import CelebrationOverlay from "@/components/CelebrationOverlay";
import JuiceLayer from "@/components/JuiceLayer";
import TierAttribute from "@/components/TierAttribute";
// Feb 2026 · Catch-all 404 — redirects any unmatched path to Home
// with a soft toast so brand-new visitors never see a blank screen.
function NotFoundRedirect() {
  useEffect(() => {
    try { toast(`That page moved — sending you home.`); } catch { /* ignore */ }
  }, []);
  return <Navigate to="/" replace />;
}

// Dev-only: a route that throws during render so we can verify the
// RouteErrorBoundary shows its friendly recovery card (never a blank
// screen). Excluded from production bundles via the NODE_ENV guard on
// the route below.
function CrashTest() {
  throw new Error("Intentional crash for RouteErrorBoundary verification");
}




function App() {
  // First-30s engagement tracker — instruments landed / first_tap /
  // left_30s_no_interaction so we can measure the exact drop-off
  // window called out in the Feb 2026 engagement audit.
  useFirstThirtySeconds();

  // Fire a session_start event on every fresh app load. Drives DAU / MAU /
  // retention counts on the backend `events` collection. No PII — uses an
  // anonymous browser-scoped UUID (see /lib/analytics.js).
  useEffect(() => {
    try {
      trackEvent("session_start", {
        lang: (typeof navigator !== "undefined" && navigator.language) || "",
        ref: document.referrer ? new URL(document.referrer).hostname : "",
      });
    } catch {/* ignore */}
    // Attention-flow (Phase B) — mark cold-open so the first tap /
    // first activity can be measured as a taps_to_first_activity event.
    try { noteAppOpen(); } catch { /* ignore */ }
    // Warm the TTS voice list so the first "Hear the word" click uses
    // the high-quality named voice (Chrome/Android need this because
    // getVoices() is empty until the async list loads).
    warmVoices();
  }, []);
  return (
    <div className="App">
      {/* Feb 2026 motion tokens (per design brief):
          • Micro interactions: 150ms
          • Celebration moments (quest complete, streak): 400ms bouncy
          • Users with prefers-reduced-motion get "always" — framer skips
            every non-essential animation automatically. Plain CSS
            transitions are gated by the global rule in index.css. */}
      <MotionConfig reducedMotion="user" transition={{ duration: 0.15, ease: [0.22, 1, 0.36, 1] }}>
      {/* Global Radix Tooltip provider — hover-hints on tiles and
          activity cards mount their own <Tooltip> under this
          provider. Shared delay so all hints feel consistent. */}
      <TooltipProvider delayDuration={200} skipDelayDuration={100}>
      <BrowserRouter>
        <ScrollToTop />
        <GAPageviewTracker />
        <ActivationTracker />
        <MilestoneCelebrator />
        <EngagementTracker />
        <Canonical />
        <Suspense fallback={
          <div
            data-testid="route-loading"
            className="min-h-[85vh] flex items-start justify-center pt-24 text-slate-500 font-bold"
          >
            <span className="animate-pulse text-4xl" aria-hidden>🌍</span>
          </div>
        }>
        <Routes>
          <Route path="/teacher-studio" element={<TeacherStudio />} />
          <Route element={<Layout />}>
            <Route path="/" element={<TodayHome />} />
            <Route path="/home/classic" element={<Home />} />
            {/* Jun 2026 content audit — repair dead links found by the route/link scan */}
            <Route path="/academy/maths" element={<Navigate to="/academy/maths/library" replace />} />
            <Route path="/code-quest" element={<Navigate to="/learn/code-quest" replace />} />
            <Route path="/games/spell" element={<Navigate to="/spell" replace />} />
            <Route path="/math-snap" element={<Navigate to="/snap" replace />} />
            <Route path="/wonders" element={<Navigate to="/explore/wonders" replace />} />
            <Route path="/letters" element={<Navigate to="/reading" replace />} />
            <Route path="/library" element={<Navigate to="/subjects" replace />} />
            <Route path="/learn/reading/sight" element={<Navigate to="/learn/reading" replace />} />
            <Route path="/admin/outcomes" element={<Navigate to="/curriculum/gaps" replace />} />
            <Route path="/admin/topic-videos" element={<Navigate to="/admin/videos" replace />} />
            <Route path="/learn-pathways" element={<LearnPathways />} />
            <Route path="/learn-pathways/:pathway" element={<LearnPathways />} />
            <Route path="/units/:unitId" element={<UnitPage />} />
            <Route path="/missions/:missionId" element={<MissionPlayer />} />
            <Route path="/missions/:missionId/:stage" element={<MissionPlayer />} />
            <Route path="/diagnostic/:subject" element={<Diagnostic />} />
            <Route path="/play-create" element={<PlayCreate />} />
            <Route path="/my-quest" element={<MyQuest />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/curriculum/explorer" element={<CurriculumExplorer />} />
            <Route path="/grown-ups" element={<GrownupGateway />} />
            <Route path="/grown-ups/:tab" element={<GrownUps />} />
            <Route path="/ai-safety" element={<AISafety />} />
            <Route path="/guides" element={<GuidePage />} />
            <Route path="/guides/:role" element={<GuidePage />} />
            <Route path="/world-trip" element={<WorldTrip />} />
            <Route path="/join/class" element={<StudentJoin />} />
            <Route path="/my-learning" element={<MyLearning />} />
            <Route path="/marketing" element={<HomeMarketing />} />
            <Route path="/learn" element={<LearnCountries />} />
            <Route path="/globe" element={<Globe3D />} />
            <Route path="/continents" element={<ContinentMaps />} />
            <Route path="/coloring" element={<Coloring />} />
            <Route path="/puzzles" element={<Puzzles />} />
            <Route path="/games" element={<Games />} />
            <Route path="/math" element={<MathGeography />} />
            <Route path="/trip" element={<Navigate to="/dream-trip" replace />} />
            <Route path="/advanced" element={<AdvancedHub />} />
            <Route path="/stickers" element={<Stickers />} />
            <Route path="/passport" element={<Passport />} />
            <Route path="/campaigns" element={<Campaigns />} />
            <Route path="/campaigns/:id" element={<Campaigns />} />
            <Route path="/adventure/:code" element={<Adventure />} />
            <Route path="/ask" element={<AskTheGlobe />} />
            <Route path="/compass" element={<Compass />} />
            {/* Part 1 (Feb 2026) — canonical redirects so the approved
                landing-page button set always resolves to the same page
                as the internal route. Old links keep working. */}
            <Route path="/daily" element={<Navigate to="/daily-review" replace />} />
            <Route path="/learning-plan" element={<Navigate to="/parents/plan" replace />} />
            <Route path="/learn-countries" element={<Navigate to="/learn" replace />} />
            <Route path="/papers" element={<Navigate to="/teacher/paper" replace />} />
            <Route path="/dream-trip" element={<DreamTrip />} />
            <Route path="/quiz" element={<Quiz />} />
            <Route path="/first-quest" element={<FirstQuest />} />
            <Route path="/tic-tac-toe" element={<TicTacToe />} />
            {/* Batch 1 · Feb 2026 · normalise routing so every game
                lives under /games/... — keep legacy /tic-tac-toe alive
                for old bookmarks + link cards but the catalog now
                points at /games/tic-tac-toe. */}
            <Route path="/games/tic-tac-toe" element={<TicTacToe />} />
            <Route path="/kaleidoscope" element={<Kaleidoscope />} />
            <Route path="/pixel-art" element={<Navigate to="/coloring?mode=pixel" replace />} />
            <Route path="/squishy" element={<ArcadeIframeGame src="/games/squishy-hop-plus.html" title="Squishy Hop+" gameId="squishy" />} />
            <Route path="/games/squishy" element={<ArcadeIframeGame src="/games/squishy-hop-plus.html" title="Squishy Hop+" gameId="squishy" />} />
            <Route path="/snap" element={<MathSnap />} />
            <Route path="/spell" element={<SpellCountries />} />
            <Route path="/geo-lines" element={<GeoLines />} />
            <Route path="/maths-academy" element={<MathsAcademy />} />
            <Route path="/academy/maths/library" element={<MathsAcademyLibrary />} />
            {/* Feb 2026 merge — `/knowledge` now redirects to the
                unified `/treasure-quest`. Same data (KNOWLEDGE_SECTIONS),
                same rewards, one clear entrypoint. `KnowledgeHub` is
                retained on disk for the retired landing-page design
                but no longer routable. */}
            <Route path="/knowledge" element={<Navigate to="/treasure-quest" replace />} />
            <Route path="/knowledge/:id" element={<KnowledgeToTreasureRedirect />} />
            <Route path="/knowledge/:id/:level" element={<KnowledgeToTreasureRedirect />} />
            <Route path="/animals" element={<Animals />} />
            {/* Knowledge Quest merged into Treasure Quest (Feb 2026). Deep
                links preserve section + level via query params so old
                bookmarks still land on the same round. */}
            <Route path="/hub/:category" element={<PillarPage />} />
            <Route path="/explore" element={<PillarPage />} />
            <Route path="/play" element={<PillarPage />} />
            <Route path="/create" element={<CreateHub />} />
            <Route path="/create/draw" element={<CreateComingSoon />} />
            <Route path="/create/flag" element={<CreateComingSoon />} />
            <Route path="/create/postcard" element={<CreateComingSoon />} />
            <Route path="/create/story" element={<CreateComingSoon />} />
            <Route path="/create/gallery" element={<Navigate to="/passport" replace />} />
            <Route path="/adventure" element={<PillarPage />} />
            {/* My World — dedicated Journey + Education page (replaces the
                old PillarPage for this route so the bottom-nav "My World"
                tab lands on the new adventure-map dashboard). */}
            <Route path="/my-world" element={<MyWorld />} />
            <Route path="/explore/nature" element={<ExploreNature />} />
            <Route path="/explore/animals" element={<ExploreAnimals />} />
            <Route path="/explore/wonders" element={<ExploreWonders />} />
            <Route path="/explore/cultures" element={<ExploreCultures />} />
            <Route path="/my-world/statistics" element={<MyWorldStatistics />} />
            <Route path="/my-world/history" element={<MyWorldHistory />} />
            <Route path="/safety" element={<Safety />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/printables" element={<Printables />} />
            <Route path="/printables/:kind" element={<PrintablePack />} />
            <Route path="/language/:code" element={<LanguageLesson />} />
            <Route path="/country/:slug" element={<CountryPage />} />
            <Route path="/parents" element={<Parents />} />
            {/* Jun 2026 · Public audience aliases — clear /for-* routes that
                improve (not duplicate) the existing parent/teacher pages. */}
            <Route path="/for-parents" element={<Navigate to="/parents" replace />} />
            <Route path="/for-teachers" element={<Navigate to="/teacher" replace />} />
            <Route path="/parents/verify" element={<ParentVerify />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/scheme-of-work" element={<Navigate to="/parents/schedule?view=annual" replace />} />
            <Route path="/weekly-plan" element={<Navigate to="/parents/schedule?view=annual" replace />} />
            <Route path="/me" element={<Navigate to="/passport" replace />} />
            <Route path="/dashboard" element={<Navigate to="/passport" replace />} />
            <Route path="/parents/plan" element={<LearningPlan />} />
            <Route path="/parents/programs" element={<StudyProgram />} />
            <Route path="/parents/plan/print/:childId" element={<PrintablePlan />} />
            <Route path="/parents/plan/print" element={<PrintablePlan />} />
            <Route path="/games/duel/quiz" element={<DuelQuiz />} />
            <Route path="/games/duel/tables" element={<DuelTables />} />
            <Route path="/games/duel/spell" element={<DuelSpell />} />
            <Route path="/games/duel/continent" element={<DuelContinent />} />
            <Route path="/games/duel/reaction" element={<DuelReaction />} />
            <Route path="/parents/report" element={<TermReport />} />
            <Route path="/parents/proof" element={<ParentProof />} />
            <Route path="/proof/:token" element={<EvidencePage />} />
            {/* Quest Academy — structured mastery courses */}
            <Route path="/academy" element={<AcademyHome />} />
            <Route path="/quest-academy" element={<Navigate to="/academy" replace />} />
            <Route path="/academy/:subjectSlug/:courseSlug" element={<AcademyCourse />} />
            <Route path="/academy/:subjectSlug/:courseSlug/placement" element={<AcademyPlacement />} />
            <Route path="/academy/:subjectSlug/:courseSlug/certificate" element={<AcademyCertificate />} />
            <Route path="/academy/:subjectSlug/:courseSlug/:unitSlug/unit-test" element={<AcademyUnitTest />} />
            <Route path="/academy/:subjectSlug/:courseSlug/:unitSlug/:skillSlug" element={<AcademySkill />} />
            {/* SEO landing pages — curriculum / country / age */}
            <Route path="/curriculum/coverage" element={<CurriculumCoverage />} />
            <Route path="/curriculum"        element={<CurriculumBrowser />} />
            {/* Canonical Subject → Stage → Topic → Lesson hierarchy */}
            <Route path="/learning"          element={<LearningHub />} />
            <Route path="/subjects"          element={<SubjectsBrowser />} />
            <Route path="/subjects/:subject" element={<SubjectsBrowser />} />
            <Route path="/topics"            element={<Navigate to="/subjects" replace />} />
            <Route path="/curriculum/gaps"   element={<CurriculumGaps />} />
            <Route path="/curriculum-landing" element={<CurriculumIndex />} />
            <Route path="/admin/video-studio" element={<VideoStudio />} />
            <Route path="/language-quest" element={<LanguageQuest />} />
            <Route path="/times-tables-quest/check" element={<MtcCheck />} />
            <Route path="/times-tables-quest" element={<TimesTablesQuest />} />
            {/* Jun 2026 identity audit: one canonical English Progress Check route; legacy deep links redirect. */}
            <Route path="/language-quest/english-quest" element={<Navigate to="/english-assessment" replace />} />
            <Route path="/language-quest/methodology" element={<Navigate to="/english-assessment/methodology" replace />} />
            <Route path="/english-assessment" element={<EnglishQuest />} />
            <Route path="/english-assessment/methodology" element={<EnglishMethodology />} />
            <Route path="/curriculum/:slug"  element={<CurriculumLanding />} />
            <Route path="/for-kids-in"       element={<CountryIndex />} />
            <Route path="/for-kids-in/:slug" element={<CountryLanding />} />
            <Route path="/for-age"           element={<AgeIndex />} />
            <Route path="/for-age/:n"        element={<AgeLanding />} />
            {/* Everything-in-one-place hub + custom live quest builder */}
            <Route path="/everything"       element={<ExploreHub />} />
            <Route path="/find"             element={<FindSomething />} />
            <Route path="/focus"            element={<FocusDay />} />
            <Route path="/start/:subject"   element={<FocusStart />} />
            <Route path="/live/build"        element={<LiveQuestBuilder />} />
            <Route path="/parents/schedule" element={<Schedule />} />
            <Route path="/parents/schedule/:childId" element={<Schedule />} />
            <Route path="/teacher"  element={<TeacherDashboard />} />
            <Route path="/teachers" element={<Navigate to="/teacher" replace />} />
            <Route path="/teachers/console" element={<TeacherConsole />} />
            <Route path="/tutor/console" element={<TutorConsole />} />
            <Route path="/teacher-dashboard" element={<TeacherDashboard />} />
            <Route path="/parents/dashboard" element={<ParentDashboard />} />
            <Route path="/discover" element={<DiagnosticQuest />} />
            <Route path="/parents/diagnostic" element={<DiagnosticQuest />} />
            <Route path="/im-new" element={<KidJoin />} />
            <Route path="/kid/join" element={<KidJoin />} />
            <Route path="/admin/curriculum" element={<AdminCurriculum />} />
            <Route path="/admin/curriculum-coverage" element={<AdminCurriculumCoverage />} />
            <Route path="/admin/lesson-integrity" element={<AdminLessonIntegrity />} />
            <Route path="/admin/live-today" element={<AdminLiveToday />} />
            <Route path="/admin/futureready" element={<AdminFutureReady />} />
            <Route path="/languages" element={<Languages />} />
            <Route path="/language-academy" element={<Academy />} />
            <Route path="/language-academy/:lang" element={<Academy />} />
            <Route path="/language-academy/:lang/:unitId" element={<AcademyUnit />} />
            <Route path="/language-academy/:lang/:unitId/:stage" element={<AcademyUnit />} />
            <Route path="/learn-topics" element={<LearnTopics />} />
            <Route path="/learn-topics/:code/test" element={<OutcomeTest />} />
            <Route path="/lesson/:code" element={<LessonReader />} />
            {/* Legacy syllabus-point topic test retired Oct 2026 — Practice/Master (Academy) and /learn-topics/:code/test are the checks. */}
            <Route path="/topic-test/*" element={<Navigate to="/learn-topics" replace />} />
            <Route path="/today" element={<Navigate to="/" replace />} />
            <Route path="/travel" element={<TravelHome />} />

            <Route path="/airline" element={<TravelHome />} />
            <Route path="/travel/:key" element={<TravelCategory />} />

            <Route path="/airline/:key" element={<TravelCategory />} />
            <Route path="/admin/lessons" element={<AdminLessons />} />
            <Route path="/admin/learning-config" element={<AdminLearningConfig />} />
            <Route path="/admin/questions" element={<AdminQuestions />} />
            <Route path="/admin/videos" element={<AdminTopicVideos />} />
            <Route path="/admin/north-star" element={<NorthStarDashboard />} />
            <Route path="/admin/curriculum-quality" element={<CurriculumQuality />} />
            <Route path="/admin/question-bank" element={<AdminQuestionBankHealth />} />
            <Route path="/admin/metrics-definitions" element={<MetricsDefinitions />} />
            <Route path="/admin/approvals" element={<ApprovalQueue />} />
            <Route path="/admin/outreach-targets" element={<OutreachTargets />} />
            <Route path="/admin/pilot-ops" element={<PilotHealth />} />
            <Route path="/admin/fundraise-targets" element={<FundraiseTargets />} />
            <Route path="/admin/academy-coverage" element={<AcademyCoverage />} />
            <Route path="/admin" element={<AdminHub />} />
            <Route path="/admin/inbox" element={<AdminInbox />} />
            <Route path="/games/clock" element={<ClockGame />} />
            <Route path="/clock" element={<ClockGame />} />
            <Route path="/tools" element={<ToolsHub />} />
            <Route path="/tools/number-line" element={<NumberLineTool />} />
            <Route path="/tools/place-value-chart" element={<PlaceValueTool />} />
            <Route path="/tools/fraction-wall" element={<FractionWallTool />} />
            <Route path="/tools/base-ten" element={<BaseTenTool />} />
            <Route path="/tools/bead-string" element={<BeadStringTool />} />
            <Route path="/tools/counters" element={<CountersTool />} />
            <Route path="/tools/arrays" element={<ArrayTool />} />
            <Route path="/tools/ten-frame" element={<TenFrameTool />} />
            <Route path="/tools/bar-model" element={<BarModelTool />} />
            <Route path="/tools/clock" element={<ClockTool />} />
            <Route path="/tools/daily-10" element={<Daily10 />} />
            {/* Cycle 56 · Primer extensions */}
            <Route path="/companion-picker" element={<CompanionPicker />} />
            <Route path="/early-reading" element={<EarlyReading />} />
            <Route path="/early-reading/placement" element={<PhonicsPlacement />} />
            <Route path="/early-reading/passage/:id" element={<DecodablePassage />} />
            <Route path="/values" element={<ValuesJourney />} />
            <Route path="/family/learner" element={<ParentLearnerView />} />
            <Route path="/live/host" element={<LiveQuestHost />} />
            <Route path="/teacher/weekly-plan" element={<TeacherWeeklyPlan />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/spelling-bee" element={<SpellingBee />} />
            <Route path="/mandala" element={<Mandala />} />
            <Route path="/dodge" element={<ArcadeIframeGame src="/games/dodge-plus.html" title="Dodge+" gameId="dodge" />} />
            {/* Batch 1 · Feb 2026 · normalised /games/... alias. */}
            <Route path="/games/dodge" element={<ArcadeIframeGame src="/games/dodge-plus.html" title="Dodge+" gameId="dodge" />} />
            {/* Legacy React implementations kept mounted at /dodge-classic and
                /squishy-classic in case a bookmark ever needs the earlier
                version. */}
            <Route path="/dodge-classic" element={<Dodge />} />
            <Route path="/squishy-classic" element={<SquishyHop />} />
            <Route path="/play/dodge" element={<DodgePublic />} />
            <Route path="/play/match-pairs"  element={<MatchPairsPublic />} />
            <Route path="/play/word-builder" element={<WordBuilderPublic />} />
            <Route path="/play/sequence-it"  element={<SequenceItPublic />} />
            <Route path="/play/label-it"     element={<LabelItPublic />} />
            <Route path="/play/speed-tables" element={<SpeedTablesPublic />} />
            <Route path="/games/match-pairs"  element={<MatchPairsGame />} />
            <Route path="/games/word-builder" element={<WordBuilderGame />} />
            <Route path="/games/sequence-it"  element={<SequenceItGame />} />
            <Route path="/games/label-it"     element={<LabelItGame />} />
            <Route path="/games/sign-match"   element={<SignMatchGame />} />
            <Route path="/games/sign-sprint"  element={<SignSprintGame />} />
            <Route path="/learn/sign"         element={<SignLanguage />} />
            <Route path="/learn/sign/:language"                       element={<SignLanguage />} />
            <Route path="/learn/sign/:language/:category"             element={<SignLesson />} />
            <Route path="/learn/reading"                             element={<PhonicsCourse />} />
            <Route path="/learn/reading/stage/:n"                    element={<PhonicsStage />} />
            <Route path="/learn/life-skills"                         element={<LifeSkills />} />
            <Route path="/learn/life-skills/:unitId"                 element={<LifeSkills />} />
            <Route path="/learn/money-time"                          element={<MoneyTime />} />
            <Route path="/learn/handwriting"                         element={<Handwriting />} />
            <Route path="/learn/code-quest"                          element={<CodeQuest />} />
            <Route path="/learn/ai"                                  element={<LearnAI />} />
            <Route path="/learn/ai/pattern-trainer"                   element={<PatternTrainer />} />
            <Route path="/learn/ai/fair-or-unfair"                    element={<FairOrUnfair />} />
            <Route path="/learn/ai/prompt-builder"                    element={<PromptBuilder />} />
            <Route path="/learn/ai/train-the-robot"                   element={<TrainTheRobot />} />
            <Route path="/learn/ai/ai-detective"                      element={<AIDetective />} />
            <Route path="/learn/ai/human-or-ai"                       element={<HumanOrAI />} />
            <Route path="/games/key-quest"                           element={<KeyQuestGame />} />
            <Route path="/games/speed-tables" element={<SpeedTablesGame />} />
            <Route path="/treasure-quest" element={<TreasureQuest />} />
            <Route path="/name-place-animal" element={<NamePlaceAnimal />} />
            <Route path="/stained-glass" element={<StainedGlass />} />
            <Route path="/parents/design-preview" element={<DesignPreview />} />
            <Route path="/quest-cards" element={<QuestCards />} />
            <Route path="/mastery-trial" element={<MasteryTrial />} />
            <Route path="/live" element={<LiveQuest />} />
            <Route path="/live/solo" element={<LiveQuestSolo />} />
            <Route path="/live/daily" element={<DailyChallenge />} />
            <Route path="/live/tournament" element={<Tournament />} />
            <Route path="/code-breaker" element={<CodeBreakerHome />} />
            <Route path="/code-breaker/daily" element={<CodeBreakerPlay />} />
            <Route path="/code-breaker/play/:subject" element={<CodeBreakerPlay />} />
            <Route path="/live/room/:code" element={<LiveRoom />} />
            <Route path="/teacher/classes" element={<TeacherConsole />} />
            <Route path="/class/join" element={<ClassJoin />} />
            <Route path="/signin" element={<SignInGate />} />
            <Route path="/family" element={<Family />} />
            {/* Feb 2026 · Cycle 63 · Part 10 — grown-up story-arc picker. */}
            <Route path="/family/story-arcs" element={<StoryArcsAdmin />} />
            {/* Cycle 63 · Part 14 — subtle age-tier switcher accessible
                from the ChildSwitcher popover. Lets a growing child
                bump between Seedlings / Explorers / Pioneers. */}
            <Route path="/age" element={<AgePicker />} />
            {/* Cycle 63 · Part 14 · Command 3 · World-as-Curriculum — a
                single place hub that opens all subjects taught through
                the given country. */}
            <Route path="/place/:code" element={<PlaceHub />} />
            {/* Cycle 63 · Part 15 · Command 4 · Homeschool + Teacher
                Layer. Never appears in child bottom nav — reachable
                from /family and from the Grown-ups menu only.
                Mounted at /homeschool because /teachers is already
                taken by the existing TeacherDashboard/Console pages. */}
            <Route path="/homeschool" element={<TeacherHub />} />
            <Route path="/homeschool/planner" element={<TeacherPlanner />} />
            <Route path="/homeschool/print" element={<TeacherPrint />} />
            <Route path="/homeschool/report" element={<TeacherReport />} />
            {/* Feb 2026 · CRO refresh — public B2B landing page. */}
            <Route path="/for-schools" element={<Navigate to="/schools" replace />} />
            {/* Feb 2026 · CRO refresh — public impact-numbers page. */}
            <Route path="/impact" element={<Impact />} />
            {/* Cycle 64.2 — Schools admin dashboard. */}
            <Route path="/schools/:slug/admin" element={<SchoolAdmin />} />
            <Route path="/schools/admin" element={<SchoolAdmin />} />
            <Route path="/schools/demo" element={<SchoolAdmin demo />} />
            <Route path="/schools/setup" element={<SchoolOnboarding />} />
            <Route path="/teachers/build-week" element={<TeacherBuildWeek />} />
            <Route path="/teachers/help" element={<TeacherHelp />} />
            {/* Stories Hub — canonical read-aloud story library. */}
            <Route path="/stories" element={<Stories />} />
            <Route path="/story/deck" element={<Navigate to="/story/the-lost-explorer" replace />} />
            <Route path="/story/:seriesSlug/:episodeSlug" element={<StoryReader />} />
            <Route path="/story/:seriesSlug" element={<StoryReader />} />
            <Route path="/admin/experience" element={<ExperienceQuality />} />
            {/* Feb 2026 · Home Reshuffle · "Plan" top-nav destination. */}
            <Route path="/plan" element={<MyPlan />} />
            <Route path="/teacher/paper" element={<QuestionPaper />} />
            <Route path="/paper/:slug" element={<PaperViewer />} />
            <Route path="/link-device" element={<LinkDevice />} />
            <Route path="/join" element={<JoinWithCode />} />
            <Route path="/daily-review" element={<DailyReview />} />
            <Route path="/class/:classId/island" element={<ClassIsland />} />
            <Route path="/lessons" element={<LessonBrowser />} />
            <Route path="/share" element={<Share />} />
            <Route path="/achievements" element={<CertificateHall />} />
            <Route path="/certificate/share/:token" element={<CertificateShare />} />
            <Route path="/certificates" element={<Navigate to="/achievements" replace />} />
            <Route path="/parents/adventure-pass/:childId" element={<AdventurePass />} />
            <Route path="/our-philosophy" element={<OurPhilosophy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/feedback" element={<Contact />} />
            <Route path="/practice/:code" element={<Practice />} />
            {/* Jun 2026 · Live teaching withdrawn from public use (safeguarding).
                /real-teachers renders a "coming soon" placeholder; all booking,
                matching, application and profile routes redirect to it so no
                child-to-adult interaction flow is publicly reachable. Admin
                routes remain typed-URL only (not linked publicly). */}
            <Route path="/real-teachers" element={<RealTeachers />} />
            <Route path="/real-teachers/apply" element={<Navigate to="/real-teachers" replace />} />
            <Route path="/real-teachers/admin" element={<RealTeacherAdmin />} />
            <Route path="/real-teachers/admin/videos" element={<TeacherVideoAdmin />} />
            <Route path="/help" element={<HelpHub />} />
            <Route path="/help/:role" element={<HelpHub />} />
            {/* Feb 2026 fixes for orphaned links flagged by user audit:
                `/parents-teachers` → `/help/parents` (Guide Hub)
                `/pwa-help` → same Help Hub, no dedicated page yet. */}
            <Route path="/parents-teachers" element={<Navigate to="/help/parents" replace />} />
            <Route path="/pwa-help" element={<Navigate to="/help/parents" replace />} />
            <Route path="/about" element={<About />} />
            <Route path="/partners/travel-mode" element={<PartnersTravelMode />} />
            <Route path="/partners" element={<Navigate to="/partners/travel-mode" replace />} />
            <Route path="/schools" element={<Schools />} />
            {process.env.NODE_ENV !== "production" && (
              <Route path="/dev/crash" element={<CrashTest />} />
            )}
            {/* Cycle 1 · Leveled Reading routes · Jun 2026 · /reading is
                now the Reading Quest hub (leveled shelf); placement lives
                at /reading/placement. */}
            <Route path="/reading" element={<ReadingHub />} />
            <Route path="/reading/placement" element={<ReadingPlacement />} />
            <Route path="/reading/:slug" element={<ReadingSession />} />
            <Route path="/draw-along" element={<DrawAlongIndex />} />
            <Route path="/draw-along/:id" element={<DrawAlong />} />
            <Route path="/real-teachers/presets" element={<Navigate to="/real-teachers" replace />} />
            <Route path="/real-teachers/my-bookings" element={<Navigate to="/real-teachers" replace />} />
            <Route path="/real-teachers/portal" element={<Navigate to="/real-teachers" replace />} />
            <Route path="/real-teachers/bookings/:id" element={<Navigate to="/real-teachers" replace />} />
            <Route path="/real-teachers/:id" element={<Navigate to="/real-teachers" replace />} />
            <Route path="/sitemap" element={<SiteMapPage />} />
            {/* Friendly in-Layout 404 — search + popular features, never blank. */}
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/student/:token" element={<StudentPlan />} />
          {/* Feb 2026 · Catch-all 404 — previously an unregistered
              path (e.g. an old CTA pointing to /math-snap before we
              renamed it to /snap) rendered a blank white screen. Now
              any unmatched route redirects to Home with a soft toast. */}
          <Route path="*" element={<NotFoundRedirect />} />
        </Routes>
        </Suspense>
        <AgeSelector />
        <BornInPicker />
        {/* Feb 2026 (user request): Globy floating-globe companion removed
            from every page — the widget sat at the bottom-left corner and
            felt visually noisy on mobile. Import is kept above so if we
            ever want it back it's a one-line re-enable. */}
        {/* <Globy /> */}
        <CelebrationOverlay />
        <JuiceLayer />
        <TierAttribute />
        <CertificateModal />
        <MilestoneCertificate />
      </BrowserRouter>
      <InstallAppPrompt />
      {/* Feb 2026: global daily-points celebration overlay — listens
          for the `mwq-daily-points` event fired from every point-earn
          call site (see `lib/dailyPoints.js`). Also shows a one-off
          recap card the first time a child returns after a new day. */}
      <DailyPointsMilestones />
      {/* Feb 2026: sound toggle moved INTO the Layout header row (next
          to the age chip). The old floating fixed pill was causing
          overlaps with the mobile menu button — removed. */}
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "#FFFFFF",
            color: "#0F172A",
            border: "3px solid #0F172A",
            borderRadius: "16px",
            fontWeight: "700",
            boxShadow: "0 4px 0 0 #0F172A",
          },
        }}
      />
      </TooltipProvider>
      </MotionConfig>
    </div>
  );
}

export default App;
